import React from 'react'
import { Row, Col, Typography, Card, Modal, List, Form, Input, Radio, Upload, Button } from 'antd'
import { ExclamationCircleOutlined, UploadOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { Editor } from '@tinymce/tinymce-react'

import { useFirebase } from '../firebaseContext'
import { openNotification, normalizeStr, generateKeywords } from '../functions'
import { fetchDetail } from '../api'
import { useRedux } from '../redux'

const { Title } = Typography
const { TextArea, Search } = Input
const { confirm } = Modal

export default function Posts() {
  const [id, setId] = React.useState(null)
  const [posts, setPosts] = React.useState([])
  const [content, setContent] = React.useState('<p></p>')
  const [loading, setLoading] = React.useState(false)
  const [fileList, setFileList] = React.useState([])
  const [file, setFile] = React.useState(null)
  const [type, setType] = React.useState('medical')
  const firebase = useFirebase()
  const actions = useRedux()

  const [form] = Form.useForm()

  React.useEffect(() => {
    const ref = firebase.getCollectionRef('posts').orderBy('created', 'desc').limit(20)
    const unsuscribe = ref.onSnapshot(async snap => {
      let array = []
      actions.setAppLoading(true)
      await Promise.all(snap.docs.map(async post => {
        const item = { id: post.id, ...post.data() }
        array.push(item)
      }))
      setPosts(array)
      actions.setAppLoading(false)
    })
    return () => {
      unsuscribe()
    }
  }, [])

  const handleSearch = async search => {
    actions.setAppLoading(true)
    let qs = firebase.getCollectionRef('posts')
    let list = []
    if(search.length > 0){
      qs = qs.where('keywords', 'array-contains', normalizeStr(search))
    }
    const snap = await qs.orderBy('created', 'desc').limit(10).get()
    snap.forEach(doc => {
      const item = { ...doc.data(), id: doc.id }
      list.push(item)
    })
    setPosts(list)
    actions.setAppLoading(false)
  }

  const loadItem = item => {
    setId(item.id)
    setType(item.type)
    if(item.type !== 'multimedia'){
      let nfl = [{url: item.image}]
      setContent(item.content)
      setFileList(nfl)
    } else {
      setContent('<p></p>')
      setFileList([])
      setFile(null)
    }
    form.setFieldsValue({ title: item.title, resume: item.resume, video: item.video, type: item.type })
  }

  const resetForm = () => {
    form.resetFields()
    setId(null)
    setType('medical')
    setContent('<p></p>')
    setFileList([])
    setFile(null)
    setLoading(false)
    actions.setAppLoading(false)
  }

  const handleSubmit = values => {
    if(id){
      const form = { ...values, updated: firebase.getServerTime() }
      if (['medical', 'journalistic'].includes(form.type)) {
        setLoading(true)
        actions.setAppLoading(true)
        if(file){
          const ref = firebase.getStorageFile('/posts', file)
          ref.put(file).then(async snap => {
            const image = await snap.ref.getDownloadURL()
            const data = { ...form, keywords: generateKeywords(form.title), content: content, image: image }
            firebase.getDocRef('posts', id).update(data)
            .then(docRef => {
              openNotification(
                'success',
                'Publicaciones',
                'Publicación actualizada con éxito.'
              )
              resetForm()
            })
            .catch(error => {
              openNotification(
                'error',
                'Publicaciones',
                error.message
              )
              setLoading(false)
              actions.setAppLoading(false)
            })
          })
        } else {
          const data = { ...form, keywords: generateKeywords(form.title), content: content }
          firebase.getDocRef('posts', id).update(data)
          .then(docRef => {
            openNotification(
              'success',
              'Publicaciones',
              'Publicación actualizada con éxito.'
            )
            resetForm()
          })
          .catch(error => {
            openNotification(
              'error',
              'Publicaciones',
              error.message
            )
            setLoading(false)
            actions.setAppLoading(false)
          })
        }
      } else {
        setLoading(true)
        actions.setAppLoading(true)
        fetchDetail(form.video)
        .then(response => {
          if (response.items.length === 0){
            setLoading(false)
            actions.setAppLoading(false)
            openNotification(
              'error',
              'Publicaciones',
              'Error al obtener datos del video de YouTube.'
            )
            return
          }
          const vdd = response.items[0].snippet
          const data = { ...form, title: vdd.title, image: vdd.thumbnails.high.url, keywords: generateKeywords(vdd.title) }
          firebase.getDocRef('posts', id).update(data)
          .then(() => {
            openNotification(
              'success',
              'Publicaciones',
              'Publicación guardada con éxito.'
            )
            resetForm()
          })
          .catch(error => {
            openNotification(
              'error',
              'Publicaciones',
              error.message
            )
            setLoading(false)
            actions.setAppLoading(false)
          })
        })
        .catch(error => {
          console.log(error)
          setLoading(false)
          actions.setAppLoading(false)
          openNotification(
            'error',
            'Publicaciones',
            'Error al obtener datos del video de YouTube.'
          )
        })
      }
    } else {
      const form = { ...values, created: firebase.getServerTime() }
      if (['medical', 'journalistic'].includes(form.type)) {
        if(!file){
          openNotification(
            'info',
            'Publicaciones',
            'Para las notas médicas / periodisticas debe seleccionar una imagen.'
          )
          return
        }
        setLoading(true)
        actions.setAppLoading(true)
        const ref = firebase.getStorageFile('/posts', file)
        ref.put(file).then(async snap => {
          const image = await snap.ref.getDownloadURL()
          const data = { ...form, keywords: generateKeywords(form.title), content: content, image: image }
          firebase.getCollectionRef('posts').add(data)
          .then(docRef => {
            openNotification(
              'success',
              'Publicaciones',
              'Publicación guardada con éxito.'
            )
            resetForm()
          })
          .catch(error => {
            openNotification(
              'error',
              'Publicaciones',
              error.message
            )
            setLoading(false)
            actions.setAppLoading(false)
          })
        })
      } else {
        setLoading(true)
        actions.setAppLoading(true)
        fetchDetail(form.video)
        .then(response => {
          if (response.items.length === 0){
            setLoading(false)
            actions.setAppLoading(false)
            openNotification(
              'error',
              'Publicaciones',
              'Error al obtener datos del video de YouTube.'
            )
            return
          }
          const vdd = response.items[0].snippet
          const data = { ...form, title: vdd.title, image: vdd.thumbnails.high.url, keywords: generateKeywords(vdd.title) }
          firebase.getCollectionRef('posts').add(data)
          .then(() => {
            openNotification(
              'success',
              'Publicaciones',
              'Publicación guardada con éxito.'
            )
            resetForm()
          })
          .catch(error => {
            openNotification(
              'error',
              'Publicaciones',
              error.message
            )
            setLoading(false)
            actions.setAppLoading(false)
          })
        })
        .catch(error => {
          console.log(error)
          setLoading(false)
          actions.setAppLoading(false)
          openNotification(
            'error',
            'Publicaciones',
            'Error al obtener datos del video de YouTube.'
          )
        })
      }
    }
  }

  const handleChange = e => {
    if(e.type){
      setType(e.type)
    }
  }

  const showConfirm = item => {
    confirm({
      title: 'Publicaciones',
      icon: <ExclamationCircleOutlined />,
      content: '¿Estás seguro de eliminar está publicación?',
      okText: 'Aceptar',
      cancelText: 'Cancelar',
      onOk() {
        firebase.getDocRef('posts', item.id).delete()
        .then(() => {
          openNotification(
            'success',
            'Publicaciones',
            'Publicación eliminada correctamente.'
          )
          resetForm()
        })
        .catch(error => {
          openNotification(
            'error',
            'Publicaciones',
            error.message
          )
          setLoading(false)
        })
      },
      onCancel() {
        console.log('Canceled')
      },
    })
  }

  const humanizeType = type => {
    switch(type){
      case 'medical':
        return 'Nota médica'
      case 'journalistic':
        return 'Nota periodistica'
      default:
        return 'Contenido multimedia'
    }
  }

  return (
    <div>
      <Title level={2}>Publicaciones</Title>
      <Row>
        <Col xs={24} sm={24} md={24} lg={24} xl={16}>
          <Card>
            <Form
              name="post"
              form={form}
              initialValues={{ title: '', resume: '', video: '', type: 'medical' }}
              onValuesChange={handleChange}
              onFinish={handleSubmit}
            >
              <Form.Item name="type" style={{ marginTop: 20 }}>
                <Radio.Group>
                  <Radio.Button value="medical">Nota médica</Radio.Button>
                  <Radio.Button value="journalistic">Nota periodistica</Radio.Button>
                  <Radio.Button value="multimedia">Contenido multimedia</Radio.Button>
                </Radio.Group>
              </Form.Item>
              {type === 'multimedia' ? <React.Fragment>
                <Form.Item
                  name="video"
                  rules={[
                    { max: 12, message: 'Superaste el limite de caracteres para este campo.' },
                    { required: true, message: 'Este campo es requerido.' },
                  ]}>
                  <Input placeholder="ID Video Youtube" />
                </Form.Item>
              </React.Fragment>:
              <React.Fragment>
                <Form.Item
                  name="title"
                  rules={[
                    { max: 64, message: 'Superaste el limite de caracteres para este campo.' },
                    { required: true, message: 'Este campo es requerido.' },
                  ]}>
                  <Input placeholder="Títlulo" />
                </Form.Item>
                <Form.Item
                  name="resume"
                  rules={[
                    { max: 128, message: 'Superaste el limite de caracteres para este campo.' },
                    { required: true, message: 'Este campo es requerido.' },
                  ]}>
                  <TextArea placeholder="Resumen" rows={2} />
                </Form.Item>
                <Editor
                  apiKey="onadd2iuf3nq0fakxw1ll0r6s1f1w6m82nppswng6mz4viz5"
                  value={content}
                  initialValue={content}
                  init={{
                    height: 500,
                    menubar: false,
                    plugins: [
                      'advlist autolink lists link image charmap print preview anchor',
                      'searchreplace visualblocks code fullscreen',
                      'insertdatetime media table paste code help wordcount'
                    ],
                    toolbar:
                      'undo redo | formatselect | bold italic backcolor | \
                      alignleft aligncenter alignright alignjustify | \
                      bullist numlist outdent indent | removeformat | help'
                  }}
                  onEditorChange={e => setContent(e)}
                />
                <Upload
                  accept='image/*'
                  fileList={fileList}
                  onChange={e => {
                    setFileList(e.fileList)
                  }}
                  onRemove={file => {
                    setFile(null)
                  }}
                  beforeUpload={file =>{
                    setFile(file)
                    return false
                  }}
                  listType="picture"
                  maxCount={1}>
                  <Button icon={<UploadOutlined />} style={{ marginTop: 20 }}>Seleccionar imagen principal</Button>
                </Upload>
              </React.Fragment>}
              <Form.Item style={{ marginTop: 20 }}>
                <Button onClick={resetForm} style={{marginRight: 10}}>
                  Nuevo
                </Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {id ? 'Guardar' :'Publicar'}
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
        <Col xs={24} sm={24} md={24} lg={24} xl={8}>
          <Card>
            <Search placeholder="Buscar publicaciones" onSearch={handleSearch} enterButton />
          </Card>
          <Card>
            <List
              itemLayout="horizontal"
              dataSource={posts}
              renderItem={item => (
                <List.Item actions={[<Button type="text" onClick={() => showConfirm(item)} icon={<DeleteOutlined />} />,
                  <Button type="text" onClick={() => loadItem(item)} icon={<EditOutlined />} />]}>
                  <List.Item.Meta
                    title={item.title}
                    description={humanizeType(item.type)}
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  )
}